// src/pages/exams/PlayfulTeacher/index.jsx
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { FiSearch } from 'react-icons/fi';
import usePermission from '@/hooks/usePermission';
import { confirm } from '@/lib/confirm';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { Filter, FilterContent, FilterActions, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { ExamRowTeacher } from './ExamRowTeacher';

const STATUS_OPTIONS = [
  { id: 'Não realizada', name: 'Não realizada' },
  { id: 'Em andamento', name: 'Em andamento' },
  { id: 'Realizada', name: 'Realizada' }
];

// Mesma regra de status da tela Default (Default/exams.jsx#getStatus) — a API
// não devolve o status pronto, ele é derivado comparando published-at/
// finished-at com a data atual.
function getExamStatus(exam) {
  const actualDate = moment();
  const initialTime = exam?.['published-at'];
  const finishTime = exam?.['finished-at'];

  if (!initialTime || !finishTime) {
    return 'Em andamento';
  } else if (actualDate.isBefore(initialTime)) {
    return 'Não realizada';
  } else if (actualDate.isBetween(initialTime, finishTime)) {
    return 'Em andamento';
  } else {
    return 'Realizada';
  }
}

// Mesma integração de dados da tela Default (Default/exams.jsx + ExamCard) —
// question_books?filter[visibility][]=exam, courses/simple e
// /classrooms?simple=true. O layout é outro, mas os dados e regras
// (status derivado, exclusão, publicar/despublicar, restrição por autoria)
// são os mesmos.
export function ExamsPlayfulTeacher({ uri }) {
  const navigate = useNavigate();
  const client = useClient();

  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('blank');
  const [selectedClassroom, setSelectedClassroom] = useState('blank');
  const [selectedStatus, setSelectedStatus] = useState('blank');

  const canCreate = usePermission('Criar provas', 'Provas e simulados');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesResponse, examsResponse, classroomsResponse] = await Promise.all([client.fetch('courses/simple'), client.fetch('question_books?filter[visibility][]=exam'), client.fetch('/classrooms?simple=true')]);

        if (coursesResponse.error) {
          toast.error('Erro ao buscar cursos');
        } else {
          setCourses(coursesResponse.data || []);
        }

        if (examsResponse.error) {
          toast.error('Erro ao buscar provas');
        } else {
          setExams((examsResponse.data || []).map(exam => ({ ...exam, status: getExamStatus(exam) })));
        }

        if (classroomsResponse.error) {
          toast.error('Erro ao buscar turmas');
        } else {
          setClassrooms(classroomsResponse.data || []);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Turma dependente do curso escolhido, igual ao filtro da tela Default
  // (ExamListTab) — só mostra as turmas do curso selecionado.
  const classroomOptions = selectedCourse === 'blank' ? [] : classrooms.filter(classroom => classroom?.course?.id === parseInt(selectedCourse, 10));

  const handleCourseChange = e => {
    setSelectedCourse(e.target.value);
    setSelectedClassroom('blank');
  };

  const filteredExams = exams.filter(exam => {
    if (searchTerm !== '' && !exam.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (selectedCourse !== 'blank' && exam['course-ids'] != selectedCourse) return false;
    // Comparação por String(): "classroom-ids" vem da API como números, mas
    // selectedClassroom vem de um <select> nativo, que sempre entrega string
    // em e.target.value — .includes() direto nunca bateria (tipos diferentes).
    if (selectedClassroom !== 'blank' && !exam['classroom-ids']?.some(id => String(id) === selectedClassroom)) return false;
    if (selectedStatus !== 'blank' && exam.status !== selectedStatus) return false;
    return true;
  });

  const handleDeleteExam = async exam => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta prova?',
      description: 'Essa ação não pode ser desfeita.',
      confirmText: 'Sim',
      cancelText: 'Não'
    });

    if (!confirmed) return;

    try {
      await client.delete(['question_books', exam.id]);
      setExams(prev => prev.filter(item => item.id !== exam.id));
      toast.success('Prova excluída com sucesso!');
    } catch (e) {
      console.error(e);
      toast.error('Erro ao excluir prova');
    }
  };

  const handleTogglePublish = async exam => {
    if (!(exam['question-count'] > 0)) {
      toast.error('Adicione questões para publicar esta prova');
      return;
    }

    const nextPublished = !exam.published;

    try {
      await client.mutate(['question_books', exam.id], { published: nextPublished });
      setExams(prev => prev.map(item => (item.id === exam.id ? { ...item, published: nextPublished } : item)));
      toast.success(nextPublished ? 'Prova publicada com sucesso!' : 'Prova despublicada com sucesso!');
    } catch (e) {
      console.error(e);
      toast.error(nextPublished ? 'Erro ao publicar prova' : 'Erro ao despublicar prova');
    }
  };

  const breadCrumbs = {
    title: 'Ferramentas de Avaliação',
    nav: [
      {
        route: uri,
        name: 'Provas',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadCrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-28"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={215}
        text="Aqui estão suas provas! Acompanhe o status, gerencie questões e veja os resultados. 📝"
      />

      <Filter variant="plain">
        <FilterContent>
          <FilterInput
            label="Buscar"
            endIcon={FiSearch}
            placeholder="Buscar prova"
            type="search"
            name="examSearch"
            id="examSearch"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <FilterSelectNative
            label="Filtrar por curso"
            value={selectedCourse}
            onChange={handleCourseChange}
            blankLabel="Todos os cursos"
            options={courses}
          />

          <FilterSelectNative
            label="Filtrar por turma"
            value={selectedClassroom}
            onChange={e => setSelectedClassroom(e.target.value)}
            blankLabel="Todas as turmas"
            options={classroomOptions}
            disabled={selectedCourse === 'blank'}
          />

          <FilterSelectNative
            label="Filtrar por status"
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            blankLabel="Todos os status"
            options={STATUS_OPTIONS}
          />
        </FilterContent>

        {canCreate && (
          <FilterActions>
            <PlayfulCardButton
              className="w-auto px-6"
              onClick={() => navigate('/provas/criar/curso')}
            >
              Nova prova
            </PlayfulCardButton>
          </FilterActions>
        )}
      </Filter>

      {loading && <Loader />}

      {!loading && filteredExams.length === 0 && (
        <EmptyState
          type="data"
          variant="plain"
          className="pt-16 sm:pt-32"
        />
      )}

      {!loading && filteredExams.length > 0 && (
        <div className="relative flex flex-col gap-3">
          {filteredExams.map((exam, index) => (
            <ExamRowTeacher
              key={exam.id}
              exam={exam}
              index={index}
              onDelete={handleDeleteExam}
              onTogglePublish={handleTogglePublish}
            />
          ))}
        </div>
      )}
    </DashboardContent>
  );
}
